import { useState } from 'react';
import MetricCard from '../MetricCard';
import ChartCard from '../charts/ChartCard';
import CustomTooltip from '../charts/CustomTooltip';
import TimePeriodToggle from './TimePeriodToggle';
import { formatNumber } from '../../utils/formatters';
import { groupByTimePeriod } from '../../utils/timePeriod';
import { CHART_COLORS, CHART_THEME } from '../../utils/chartColors';
import {
  FiDollarSign,
  FiArrowDownLeft,
  FiZap,
  FiFileText
} from 'react-icons/fi';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

const INVOICE_STATUS_COLORS = {
  paid: '#22c55e',
  open: '#3b82f6',
  void: '#94a3b8',
  uncollectible: '#ef4444',
  draft: '#f59e0b',
  unknown: '#d1d5db',
};

const RevenueTab = ({ data }) => {
  const [timePeriod, setTimePeriod] = useState('monthly');

  if (!data) return null;

  const { revenue, timeSeries } = data;

  // Group timeSeries for revenue trend
  const grouped = groupByTimePeriod(timeSeries || [], timePeriod);
  const revenueTrend = grouped.map(d => ({
    period: d.period,
    collected: d.collected,
    refunded: d.refunded,
    net: d.collected - d.refunded,
  }));

  // Collection rate trend (always monthly from backend)
  const collectionRateTrend = revenue?.collectionRateTrend || [];

  // Revenue by interval for horizontal bar
  const intervalData = Object.entries(revenue?.byInterval || {}).map(([interval, d]) => ({
    interval: interval.charAt(0).toUpperCase() + interval.slice(1) + 'ly',
    count: d.count,
    mrr: d.totalMonthly
  })).sort((a, b) => b.mrr - a.mrr);

  // Invoice status for donut
  const invoiceStatusData = Object.entries(revenue?.invoicesByStatus || {})
    .map(([status, d]) => ({
      name: status.charAt(0).toUpperCase() + status.slice(1),
      value: d.count,
      amount: Math.round(d.amount / 100),
      color: INVOICE_STATUS_COLORS[status] || INVOICE_STATUS_COLORS.unknown
    }))
    .filter(d => d.value > 0);

  const totalInvoices = invoiceStatusData.reduce((sum, d) => sum + d.value, 0);
  const currencyFormatter = (val) => `$${val.toLocaleString()}`;
  const isDailyView = timePeriod === 'daily';

  return (
    <div className="space-y-6">
      {/* Metric Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Total Collected"
          value={`$${formatNumber(revenue.totalCollected)}`}
          icon={FiDollarSign}
        />
        <MetricCard
          label="Total Refunded"
          value={`$${formatNumber(revenue.totalRefunded)}`}
          icon={FiArrowDownLeft}
        />
        <MetricCard
          label="Net Revenue"
          value={`$${formatNumber(revenue.netRevenue)}`}
          icon={FiZap}
        />
        <MetricCard
          label="Avg Invoice"
          value={`$${formatNumber(Math.round(revenue.avgInvoiceValue / 100))}`}
          subtitle={`${formatNumber(revenue.invoiceCount)} paid invoices`}
          icon={FiFileText}
        />
      </div>

      {/* Time Period Toggle */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700">Revenue Trends</h3>
        <TimePeriodToggle value={timePeriod} onChange={setTimePeriod} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Trend */}
        <ChartCard title="Revenue Trend" subtitle={`Collected vs net revenue — ${timePeriod}`}>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={revenueTrend}>
              <defs>
                <linearGradient id="revAreaGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.15} />
                  <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...CHART_THEME.grid} />
              <XAxis dataKey="period" tick={CHART_THEME.axis} angle={isDailyView ? -45 : 0} textAnchor={isDailyView ? 'end' : 'middle'} height={isDailyView ? 50 : 30} />
              <YAxis tick={CHART_THEME.axis} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip content={<CustomTooltip formatter={currencyFormatter} />} />
              <Legend iconType="circle" iconSize={8} />
              <Area
                type="monotone"
                dataKey="net"
                stroke={CHART_COLORS.primary}
                strokeWidth={2}
                fill="url(#revAreaGradient)"
                name="Net Revenue"
                dot={false}
              />
              <Area
                type="monotone"
                dataKey="collected"
                stroke={CHART_COLORS.info}
                strokeWidth={1.5}
                fill="none"
                name="Collected"
                strokeDasharray="4 4"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Revenue by Billing Interval */}
        <ChartCard title="Revenue by Interval" subtitle="MRR contribution by billing cadence">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={intervalData} layout="vertical" barSize={28}>
              <CartesianGrid {...CHART_THEME.grid} horizontal={false} />
              <XAxis type="number" tick={CHART_THEME.axis} tickFormatter={(v) => `$${v.toLocaleString()}`} />
              <YAxis type="category" dataKey="interval" tick={CHART_THEME.axis} width={80} />
              <Tooltip content={<CustomTooltip formatter={(val, name) => name === 'MRR' ? `$${val.toLocaleString()}/mo` : val} />} />
              <Bar dataKey="mrr" fill={CHART_COLORS.primary} name="MRR" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Invoice Status Breakdown */}
        <ChartCard title="Invoice Status" subtitle="Distribution of all invoices by status">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={invoiceStatusData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={3}
                strokeWidth={0}
              >
                {invoiceStatusData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0];
                  return (
                    <div className="bg-slate-800 text-slate-50 px-3 py-2 rounded-lg shadow-lg text-xs">
                      <p className="font-medium mb-1">{d.name}</p>
                      <p className="text-slate-300">{d.value} invoices ({((d.value / totalInvoices) * 100).toFixed(1)}%)</p>
                      <p className="text-slate-300">${d.payload.amount.toLocaleString()}</p>
                    </div>
                  );
                }}
              />
              <Legend iconType="circle" iconSize={8} />
              <text x="50%" y="47%" textAnchor="middle" className="text-2xl font-semibold fill-gray-900">
                {formatNumber(totalInvoices)}
              </text>
              <text x="50%" y="55%" textAnchor="middle" className="text-xs fill-gray-500">
                Invoices
              </text>
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Collection Rate Trend (always monthly) */}
        <ChartCard title="Collection Rate" subtitle="Percentage of invoices successfully collected">
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={collectionRateTrend}>
              <CartesianGrid {...CHART_THEME.grid} />
              <XAxis dataKey="month" tick={CHART_THEME.axis} />
              <YAxis tick={CHART_THEME.axis} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
              <Tooltip content={<CustomTooltip formatter={(val) => `${val}%`} />} />
              <ReferenceLine y={95} stroke={CHART_COLORS.muted} strokeDasharray="5 5" label={{ value: '95% target', position: 'right', fill: '#94a3b8', fontSize: 10 }} />
              <Line
                type="monotone"
                dataKey="rate"
                stroke={CHART_COLORS.success}
                strokeWidth={2}
                name="Collection Rate"
                dot={{ r: 3, fill: CHART_COLORS.success }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
};

export default RevenueTab;
